import { useMemo, useState } from 'react';
import { Box, Button, Drawer, Group, Paper, Stack, Text } from '@mantine/core';
import { PiCheckBold, PiGear } from 'react-icons/pi';
import { TOffer, TOfferItem, TSelectionBlock } from '@/models/offers';
import ProposalBlocksPreview from './ProposalBlocksPreview';
import ServiceMatrixTable, { fmtPrice } from './ServiceMatrixTable';
import ServicesManager from './ServicesManager';

// Post-selection rendering: the offer state snapshot is the single source of
// truth, so CP edits made after the customer committed never propagate here.
/**
 * What each package in a block costs, and what a block without packages comes to.
 *
 * Same rule the customer's page and the proposal PDF price a package by: every service in
 * the block that the package includes, leaving out the hidden ones and the children folded
 * into their parent. A block with no packages totals what the customer actually picked.
 */
function blockTotals(block: TSelectionBlock): (string | null)[] | null {
  if (!block.showSummaryRow) return null;
  const currency = block.services[0]?.priceCurrency ?? '';
  const sum = (keep: (svc: TSelectionBlock['services'][number]) => boolean) =>
    fmtPrice(
      block.services.reduce(
        (total, svc) =>
          keep(svc) ? total + Number(svc.priceAmount || 0) : total,
        0,
      ),
      currency,
    );

  const tiers = block.tierNames || [];
  return tiers.length === 0
    ? [sum((svc) => svc.selected)]
    : tiers.map((_, tier) =>
        sum((svc) => svc.tiers?.[tier]?.included ?? false),
      );
}

function SelectionBlocksMatrix({ blocks }: { blocks: TSelectionBlock[] }) {
  const visible = blocks.filter((b) => b.services.length > 0);
  if (visible.length === 0) return null;

  return (
    <Box pl="sm" style={{ overflowX: 'auto' }}>
      {visible.map((block) => (
        <ServiceMatrixTable
          key={block.uuid}
          title={block.title}
          tierNames={block.tierNames || []}
          selectedTier={block.selectedTier}
          totals={blockTotals(block)}
          rows={block.services.map((svc) => ({
            key: `${block.uuid}-${svc.serviceId}`,
            name: svc.serviceName,
            price: fmtPrice(svc.priceAmount, svc.priceCurrency),
            // Only a block without packages is chosen service by service; where
            // packages exist the chosen column already carries the answer.
            selected:
              (block.tierNames || []).length === 0 ? svc.selected : undefined,
            tiers: (block.tierNames || []).length
              ? (block.tierNames || []).map((_, tier) => {
                  const cell = svc.tiers?.[tier];
                  const included = cell?.included ?? false;
                  return {
                    included,
                    label:
                      (cell?.displayIcon ?? true)
                        ? undefined
                        : included
                          ? fmtPrice(svc.priceAmount, svc.priceCurrency) || ''
                          : `0 ${svc.priceCurrency}`,
                  };
                })
              : undefined,
          }))}
        />
      ))}
    </Box>
  );
}

export default function ServicesWidget({ offer }: { offer: TOffer }) {
  const isAccepted = offer.accepted;
  const hasSelections = offer.customerSelections != null;
  const [manageOpen, setManageOpen] = useState(false);

  const items = offer.items || [];
  const proposalItems = items.filter((i) => i.itemType === 'proposal');
  const standaloneItems = items.filter((i) => i.itemType === 'service');

  const selectionsByCp = useMemo(
    () =>
      new Map(
        (offer.customerSelections?.proposals || []).map((p) => [p.cpId, p]),
      ),
    [offer.customerSelections],
  );

  const purchasedServiceIds = useMemo(
    () =>
      new Set<number>(
        (offer.customerSelections?.standaloneServices || [])
          .filter((s) => s.selected)
          .map((s) => s.serviceId),
      ),
    [offer.customerSelections],
  );

  const isStandalonePurchased = (item: TOfferItem) =>
    item.serviceId != null && purchasedServiceIds.has(item.serviceId);

  return (
    <Paper withBorder p="sm" data-testid="ux-services-widget">
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Text fw={500} fz="sm">
          Services
        </Text>
        {!isAccepted && (
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            leftSection={<PiGear size={12} />}
            onClick={() => setManageOpen(true)}
          >
            Manage
          </Button>
        )}
      </Group>

      {items.length === 0 ? (
        <Text fz="xs" c="dimmed" ta="center" py="sm">
          No services added yet.
        </Text>
      ) : (
        <Stack gap={isAccepted ? 6 : 4}>
          {proposalItems.map((item) => {
            const selection =
              item.cpId != null ? selectionsByCp.get(item.cpId) : undefined;
            return (
              <Box key={item.id}>
                <ServiceRow
                  name={item.itemName || '—'}
                  price={
                    isAccepted
                      ? fmtPrice(
                          item.priceAmount != null
                            ? Number(item.priceAmount)
                            : null,
                          item.currency,
                        )
                      : null
                  }
                />
                {selection ? (
                  <SelectionBlocksMatrix blocks={selection.blocks} />
                ) : (
                  !hasSelections &&
                  item.cpId != null && (
                    <ProposalBlocksPreview
                      cpId={item.cpId}
                      currency={item.currency}
                    />
                  )
                )}
              </Box>
            );
          })}

          {standaloneItems.length > 0 && (
            <Box mt={proposalItems.length > 0 ? 4 : 0}>
              <Text fz="xs" fw={600} c="dimmed" mb={2}>
                Standalone Services
              </Text>
              <Stack gap={isAccepted ? 6 : 4}>
                {standaloneItems.map((item) => (
                  <ServiceRow
                    key={item.id}
                    name={item.itemName || '—'}
                    price={
                      isAccepted
                        ? fmtPrice(
                            item.priceAmount != null
                              ? Number(item.priceAmount)
                              : null,
                            item.currency,
                          )
                        : null
                    }
                    muted={hasSelections && !isStandalonePurchased(item)}
                    chosen={hasSelections && isStandalonePurchased(item)}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      <Drawer
        opened={manageOpen}
        onClose={() => setManageOpen(false)}
        position="right"
        size="xl"
        title="Manage Services"
      >
        <ServicesManager offer={offer} />
      </Drawer>
    </Paper>
  );
}

function ServiceRow({
  name,
  price,
  muted,
  chosen,
}: {
  name: string;
  price: string | null;
  muted?: boolean;
  /** The customer picked this standalone service. Undefined before they choose. */
  chosen?: boolean;
}) {
  return (
    <Box
      px="xs"
      py={4}
      style={{
        backgroundColor: chosen
          ? 'var(--mantine-color-green-light)'
          : undefined,
        borderRadius: chosen ? 4 : undefined,
      }}
    >
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
          {chosen && (
            <PiCheckBold
              size={12}
              color="var(--mantine-color-green-8)"
              style={{ flexShrink: 0 }}
              aria-label="Chosen"
            />
          )}
          <Text
            fz="xs"
            truncate
            fw={chosen ? 500 : undefined}
            c={chosen ? 'green.8' : muted ? 'dimmed' : undefined}
            style={{ minWidth: 0 }}
          >
            {name}
          </Text>
        </Group>
        {price && (
          <Text
            fz="xs"
            c={chosen ? 'green.8' : 'dimmed'}
            style={{ whiteSpace: 'nowrap' }}
          >
            {price}
          </Text>
        )}
      </Group>
    </Box>
  );
}
